import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import styles from "./HomepageBanner.module.scss";

export interface HomepageBannerCopy {
  title: string;
  subtitle: string;
  tag: string;
}

/** Valid icon keys that can be used in experiment JSON */
type HomepageBannerIconKey = "fire";

/** Display tag label → emoji shown beside it (keys must match experiment icon field). */
const HOMEPAGE_BANNER_TAG_ICONS: Record<HomepageBannerIconKey, string> = {
  fire: "🔥",
};

const VALID_LOCALES = ["en", "fr"] as const;

/** Experiment JSON: optional `icon`, plus per-locale banner copy (`en` / `fr`). */
export type HomepageBannerContent = Partial<
  Record<"en" | "fr", HomepageBannerCopy>
> & {
  icon?: HomepageBannerIconKey;
};

export const HomepageBanner = ({
  bannerContent,
}: {
  bannerContent: HomepageBannerContent;
}) => {
  const { t, locale } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "HomepageBanner");

  if (!bannerContent) {
    return null;
  }

  const resourceLocale =
    VALID_LOCALES.find((validLocale) =>
      locale.toLowerCase().startsWith(validLocale)
    ) || "en";

  const iconKey = bannerContent.icon ?? null;
  const icon = iconKey ? HOMEPAGE_BANNER_TAG_ICONS[iconKey] : null;
  const copy = bannerContent[resourceLocale];
  const title = copy?.title ?? t("home.homepageBanner.title");
  const subtitle = copy?.subtitle ?? t("home.homepageBanner.subtitle");
  const tag = copy?.tag ?? t("home.homepageBanner.tag");

  return (
    <div className={block}>
      <span className={cn("tag")}>
        {tag}
        {icon && <span aria-hidden="true"> {icon}</span>}
      </span>
      <p className={cn("text")}>
        <span className={cn("title")}>{title} </span>
        <span className={cn("subtitle")}>{subtitle}</span>
      </p>
    </div>
  );
};
